import React, { useEffect, useState } from 'react';
import { Linking, Modal, Pressable, View } from 'react-native';
import { StackScreenProps } from '@react-navigation/stack';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { Controller, useForm } from 'react-hook-form';
import { CheckBox, Divider } from 'react-native-elements';
import { AntDesign } from '@expo/vector-icons';
import { useResponsiveHeight } from '@/components/Util/reactResponsiveDimensions';
import { AppHeaderAction } from '@/components/AppHeader/AppHeaderAction';
import { Icon } from '@/components/Icon';
import { Text } from '@/components/Text';
import {
  addPatient,
  fetchPatient,
  updatePatient,
} from '@/api/services/patient';
import { fetchPatientsList } from '@/api/services/user';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { AvatarList } from '@/components/SharedComponents/AvatarList/AvatarList';
import { BodyWrapper } from '@/components/ui/therapist/BodyWrapper';
import { Input } from '@/components/ui/therapist/Input';
import { useNotification } from '@/components/ui/therapist/Notification/NotificationProvider';
import Card from '@/components/ui/therapist/Card';
import { Button } from '@/components/ui/therapist/Button';
import { Links } from '@/config/links';
import colors, { appTheme } from '@/config/colors';
import { hasInternetAsync } from '@/screens/HomeTherapist/helpers';
import { logError } from '@/utils/logHandling';

import { replacedPatientProfileHeader } from '@/utils/stringHelpers';
import { styles } from './styles';

type AddPatientFormData = {
  firstName: string;
  lastName: string;
  signedReleaseFromConfidentiality: boolean;
  avatar: string;
  biography: string;
  gender: string;
  diagnosis: string;
};

export default function AddPatient(
  props: StackScreenProps<TherapistStackParamList, 'AddPatientScreen'>,
) {
  const {
    control,
    handleSubmit,
    setValue,
    trigger,
    watch,
    formState: { errors, isValid },
  } = useForm<AddPatientFormData>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      signedReleaseFromConfidentiality: false,
      avatar: 'modules/patients/client/img/avatar/avatar1.png',
      biography: '',
      gender: '',
      diagnosis: '',
    },
  });
  const { showNotification } = useNotification();
  const avatar = watch('avatar');

  const [showModalToggle, setShowModalToggle] = useState(false);
  const patientId = props?.route?.params?._id;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = async (data: AddPatientFormData) => {
    try {
      setIsSubmitting(true);
      let p;
      const hasInternet = await hasInternetAsync();
      if (hasInternet) {
        if (patientId) {
          p = await updatePatient(
            {
              avatar: [data.avatar],
              biography: data.biography,
              diagnosis: data.diagnosis,
              firstName: data.firstName,
              gender: data.gender,
              lastName: data.lastName,
              signedReleaseFromConfidentiality:
                data.signedReleaseFromConfidentiality,
              _id: patientId,
            },
            patientId,
          );
        } else {
          p = await addPatient({
            ...data,
            avatar: [data.avatar],
          });
        }
        const patients = await fetchPatientsList();
        showNotification({
          type: 'success',
          title: 'Profil wurde erfolgreich gespeichert',
        });
        props.navigation.navigate('PatientProfile', {
          id: p.data._id,
          patients,
          replacedTitle: replacedPatientProfileHeader({
            patients: undefined,
            patientId: p.data._id,
            patient: p.data,
          }),
        });
      } else {
        setShowModalToggle(true);
      }
      setIsSubmitting(false);
    } catch (error) {
      setIsSubmitting(false);
      logError('Could not update patient', error);
    }
  };

  useEffect(() => {
    const findPatient = async () => {
      if (patientId) {
        try {
          let patient = await fetchPatient(patientId);
          let {
            firstName,
            lastName,
            gender,
            diagnosis,
            biography,
            avatar,
            signedReleaseFromConfidentiality,
          } = patient;

          // Set patient data to update
          setValue('firstName', firstName);
          setValue('lastName', lastName);
          setValue('gender', gender);
          setValue('avatar', avatar[0]);
          setValue('biography', biography);
          setValue('diagnosis', diagnosis);
          setValue(
            'signedReleaseFromConfidentiality',
            signedReleaseFromConfidentiality,
          );
        } catch (error) {
          logError('Could not fetch patient', error);
        }
      }
    };
    findPatient();
  }, []);

  useEffect(() => {
    props.navigation.setOptions({
      title: patientId ? 'Profil bearbeiten' : 'Profil hinzufügen',
      headerRight: patientId
        ? () => (
            <View style={{ gap: 10, flexDirection: 'row' }}>
              <AppHeaderAction
                primary
                onPress={() => props.navigation.goBack()}
                icon={<Icon name="close" scale={1.2} />}
                text="Abbrechen"
              />
              <AppHeaderAction
                primary
                onPress={handleSubmit(onSubmit)}
                icon={<AntDesign name="file1" />}
                text="Speichern"
              />
            </View>
          )
        : () => (
            <Button
              label="Abbrechen"
              icon="close"
              onPress={() => props.navigation.goBack()}
            />
          ),
    });
  }, []);

  return (
    <BodyWrapper showSidebar={false} bodyStyle={{ width: '90%', flex: 1 }}>
      <KeyboardAwareScrollView
        enableOnAndroid
        extraHeight={useResponsiveHeight(30)}
        extraScrollHeight={30}
      >
        <Card title="Profilbild">
          <View style={{ paddingBottom: 16 }}>
            <AvatarList
              defaultAvatar={avatar}
              onAvatarPress={(avatar: string) => setValue('avatar', avatar)}
            />
          </View>
        </Card>
        <Card title="Profildaten" styleBody={{ padding: 0 }}>
          <View style={{ padding: 20 }}>
            <Controller
              control={control}
              name="firstName"
              rules={{
                required: 'Bitte geben Sie den Vornamen an',
              }}
              render={({ field }) => (
                <Input
                  label="Vorname"
                  required={true}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    trigger('firstName');
                  }}
                  status={
                    errors.firstName
                      ? 'warning'
                      : field.value.length > 0
                        ? 'validated'
                        : 'default'
                  }
                  statusMessage={errors.firstName?.message}
                />
              )}
            />
          </View>
          <Divider color={colors.grey} width={1} />
          <View style={{ padding: 20 }}>
            <Controller
              control={control}
              name="lastName"
              rules={{
                required: 'Bitte geben Sie den Nachnamen an',
              }}
              render={({ field }) => (
                <Input
                  label="Nachname"
                  required={true}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    trigger('lastName');
                  }}
                  status={
                    errors.lastName
                      ? 'warning'
                      : field.value.length > 0
                        ? 'validated'
                        : 'default'
                  }
                  statusMessage={errors.lastName?.message}
                />
              )}
            />
          </View>
        </Card>
        <Card title="Zusätzliche Informationen" styleBody={{ padding: 0 }}>
          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="gender"
              render={({ field }) => (
                <Input
                  label="Geschlecht"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={() => {
                    field.onBlur();
                    trigger('gender');
                  }}
                  status={
                    errors.gender
                      ? 'warning'
                      : field.value
                        ? 'validated'
                        : undefined
                  }
                />
              )}
            />
          </View>
          <Divider color={colors.grey} width={1} />

          <View style={styles.formFieldTwoThirds}>
            <Controller
              control={control}
              name="diagnosis"
              render={({ field }) => (
                <Input
                  label="Diagnose"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  status={field.value.length > 0 ? 'validated' : 'default'}
                />
              )}
            />
          </View>
          <Divider color={colors.grey} width={1} />
          <View style={{ padding: 20 }}>
            <Controller
              control={control}
              name="biography"
              render={({ field }) => {
                const charCount = field.value.length;
                return (
                  <Input
                    label="Notizen"
                    placeholder="(optional, max. 150 Zeichen)"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    status={charCount > 0 ? 'validated' : 'default'}
                    additionalInfo={`${charCount}/150`}
                    multiline
                    numberOfLines={3}
                    maxLength={150}
                  />
                );
              }}
            />
          </View>
        </Card>
        {!patientId && (
          <View
            style={{
              flex: 1,
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Button
              look="primary"
              onPress={handleSubmit(onSubmit)}
              label="Erstellen"
              disabled={!isValid || isSubmitting}
            />
            <Controller
              control={control}
              name="signedReleaseFromConfidentiality"
              rules={{
                required: true,
              }}
              render={({ field }) => (
                <CheckBox
                  onPress={(e) => {
                    field.onChange(!field.value);
                  }}
                  checked={field.value}
                  containerStyle={{
                    backgroundColor: undefined,
                    borderWidth: 0,
                  }}
                  checkedColor={appTheme.primary}
                  title={
                    <Text style={{ marginLeft: 8 }}>
                      <Text style={styles.redText}>*</Text>
                      Ich habe zur Kenntnis genommen, dass bei der Speicherung
                      eines Patienten-Profils unter Klarnamen eine {'\n'}
                      <Text
                        onPress={() =>
                          Linking.openURL(Links.confidentiality.href)
                        }
                        style={{ color: colors.link }}
                      >
                        Schweigepflichtsentbindung
                      </Text>
                      <Text> erforderlich ist. </Text>
                    </Text>
                  }
                />
              )}
            />
          </View>
        )}
      </KeyboardAwareScrollView>

      <Modal
        supportedOrientations={[
          'landscape',
          'landscape-left',
          'landscape-right',
        ]}
        animationType="none"
        transparent={true}
        visible={showModalToggle}
        onRequestClose={() => {
          setShowModalToggle(false);
        }}
      >
        <View style={styles.centeredView}>
          <View style={styles.modalView}>
            <Text style={styles.modalTitle}>
              Keine Verbindung zum neolexon Server
            </Text>
            <View style={styles.underline} />
            <Text style={styles.modalText}>
              Bitte überprüfen Sie Ihre Internetverbindung und versuchen es
              erneut.
            </Text>
            <Pressable
              style={styles.button}
              onPress={() => setShowModalToggle(false)}
            >
              <Text style={styles.modalButton}>OK</Text>
            </Pressable>
          </View>
        </View>
      </Modal>
    </BodyWrapper>
  );
}
